import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PublicHeader, PublicFooter, ArticleCard } from '../components/PublicLayout';
import { authorsAPI } from '../lib/api';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { FileText, Eye, Award } from 'lucide-react';

export default function AuthorPage() {
  const { id } = useParams();
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAuthor(); }, [id]);

  const loadAuthor = async () => {
    setLoading(true);
    try {
      const res = await authorsAPI.get(id);
      setAuthor(res.data);
    } catch { }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <>
        <PublicHeader />
        <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8">
          <Skeleton className="h-48 w-full rounded-xl mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1,2,3].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}
          </div>
        </main>
      </>
    );
  }

  if (!author) {
    return (
      <>
        <PublicHeader />
        <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 text-center">
          <p className="text-[#5B616E]">Author not found</p>
        </main>
        <PublicFooter />
      </>
    );
  }

  return (
    <>
      <PublicHeader />
      <main className="bg-[#FBF7EF] min-h-screen">
        {/* Author Header */}
        <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-10" data-testid="author-profile">
          <div className="flex items-start gap-6">
            {author.avatar_url ? (
              <img src={author.avatar_url} alt="" className="w-20 h-20 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#B89B5E]/10 flex items-center justify-center text-3xl font-bold text-[#B89B5E] news-masthead flex-shrink-0">
                {author.full_name?.charAt(0) || '?'}
              </div>
            )}
            <div className="flex-1">
              <h1 className="news-masthead text-3xl font-bold">{author.full_name}</h1>
              <p className="text-sm text-[#5B616E] capitalize mt-1">{author.role?.replace('_', ' ')}</p>
              {author.bio && <p className="text-sm text-[#5B616E] mt-3 max-w-2xl leading-relaxed">{author.bio}</p>}
              {author.bio_extended && <p className="text-sm text-[#5B616E] mt-2 max-w-2xl leading-relaxed">{author.bio_extended}</p>}
              <div className="flex items-center gap-6 mt-4">
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-[#B89B5E]" />
                  <span className="font-medium">{author.article_count || 0}</span>
                  <span className="text-[#5B616E]">articles</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Eye className="w-4 h-4 text-[#1E5A8A]" />
                  <span className="font-medium">{(author.total_views || 0).toLocaleString()}</span>
                  <span className="text-[#5B616E]">total views</span>
                </div>
              </div>
              {author.top_categories?.length > 0 && (
                <div className="flex gap-2 mt-3">
                  {author.top_categories.map((tc, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {tc.category?.name || 'Unknown'} ({tc.count})
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Author Articles */}
        <section className="max-w-[1200px] mx-auto px-4 sm:px-6 pb-16">
          <h2 className="news-masthead text-xl font-bold mb-6">Latest Articles</h2>
          {author.recent_articles?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {author.recent_articles.map(article => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#E7E0D6] p-12 text-center text-[#5B616E]">
              <p>No published articles yet</p>
            </div>
          )}
        </section>
      </main>
      <PublicFooter />
    </>
  );
}
